import React from 'react';

import Button from 'CommonComponents/Button';

import CheckIcon from 'Assets/icons/check.svg';

const DONE = ['complete', 'not_applicable'];

/**
 * Shown after "Finish required setup". It never blocks: any required step
 * still outstanding is listed with a link back to it, under copy that does
 * not claim the essentials are done.
 *
 * continueStep comes from the server — the first optional step not started or
 * in progress, else the first deferred one — and hides "Continue setup" when
 * nothing optional is outstanding.
 */
export default function FinishScreen({ steps, projectUrl, continueStep, onSelect }) {
    const outstandingRequired = steps.filter((step) => step.group === 'required' && !DONE.includes(step.status));
    const essentialsDone = outstandingRequired.length === 0;

    return (
        <section className="tw-px-6 tw-py-8 tw-rounded-lg tw-bg-white tw-shadow-primary">
            {essentialsDone && (
                <span className="tw-flex tw-items-center tw-justify-center tw-w-14 tw-h-14 tw-rounded-full tw-bg-green tw-text-dark-green">
                    <CheckIcon className="tw-w-7 tw-h-7" />
                </span>
            )}

            <h2 className={`tw-text-3xl tw-font-bold tw-text-charcoal tw-mb-0 ${essentialsDone ? 'tw-mt-6' : ''}`}>
                {essentialsDone ? $t("You're ready to start budgeting") : $t('Almost there')}
            </h2>
            <p className="tw-mt-4 tw-mb-0 tw-max-w-2xl tw-text-base tw-text-charcoal-75">
                {essentialsDone
                    ? $t(
                          "The essentials are done: project, dates, modules, functionality, commitments, structure and users. The remaining setup pages are waiting whenever you need them; the Settings page keeps track of what's left.",
                      )
                    : $t("A few required steps still need attention before the essentials are done. The Settings page keeps track of what's left.")}
            </p>

            {!essentialsDone && (
                <div className="tw-mt-4 tw-max-w-2xl tw-p-4 tw-rounded-lg tw-border tw-border-charcoal-15">
                    <p className="tw-text-sm tw-font-semibold tw-text-charcoal tw-mb-2">{$t('These required steps still need attention:')}</p>
                    <ul className="tw-mb-0 tw-ps-0 tw-list-none tw-flex tw-flex-col tw-gap-y-1">
                        {outstandingRequired.map((step) => (
                            <li key={step.key}>
                                <button
                                    type="button"
                                    className="tw-p-0 tw-border-0 tw-bg-transparent tw-text-sm tw-font-medium tw-text-blue hover:tw-underline"
                                    onClick={() => onSelect(step.key)}
                                >
                                    {step.label}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="tw-mt-6 tw-flex tw-flex-wrap tw-gap-3">
                <Button as="a" href={projectUrl} variant="primary" data-action="navigate">
                    {$t('Go to the project')}
                </Button>
                {!!continueStep && (
                    <Button variant="outline" onClick={() => onSelect(continueStep)}>
                        {$t('Continue setup')}
                    </Button>
                )}
            </div>
        </section>
    );
}
